import type { Flashcard, Lesson, Question, Skill, Topic } from '@/data/types';
import { card, choice, example, f, numeric, p, text, tip, warn } from '../../authoring';

/**
 * Biznes i zarządzanie, dział 2: gospodarka i państwo.
 *
 * Podstawa programowa 2024: ZP III.6–III.7; ZR II.9–II.21, III.12–III.13.
 * Stawki podatków w zadaniach są podane w treści (uproszczone) — egzamin
 * sprawdza rozumienie mechanizmu, a stawki mogą się zmieniać.
 */

export const MACRO_TOPIC: Topic = {
  id: 'biz-macro-topic',
  subjectId: 'biz',
  name: 'Gospodarka i państwo',
  summary: 'PKB, inflacja i cykl koniunkturalny; budżet państwa, deficyt i dług; polityka fiskalna i monetarna (NBP, RPP); kursy walut, handel i Unia Europejska.',
};

export const MACRO_SKILLS: Skill[] = [
  {
    id: 'biz-macro-indicators',
    topicId: 'biz-macro-topic',
    name: 'PKB, inflacja i cykl koniunkturalny',
    level: 'PR',
    ckeRequirement: 'Podstawowe parametry gospodarki: PKB, inflacja, zatrudnienie; wzrost i rozwój; fazy cyklu koniunkturalnego (ZP III.6; ZR II.9–II.11)',
    prerequisites: ['biz-supply-demand'],
    examValue: 0.85,
  },
  {
    id: 'biz-state-budget',
    topicId: 'biz-macro-topic',
    name: 'Budżet państwa, deficyt i dług publiczny',
    level: 'PR',
    ckeRequirement: 'Źródła wpływów i kierunki wydatków budżetu państwa i JST, deficyt budżetowy, dług publiczny (ZP III.7)',
    prerequisites: ['biz-macro-indicators'],
    examValue: 0.7,
  },
  {
    id: 'biz-fiscal-monetary',
    topicId: 'biz-macro-topic',
    name: 'Polityka fiskalna i monetarna',
    level: 'PR',
    ckeRequirement: 'Narzędzia oddziaływania państwa na gospodarkę, podatki progresywne i liniowe, polityka monetarna NBP i RPP (ZR II.12–II.17, III.12, III.13)',
    prerequisites: ['biz-state-budget'],
    examValue: 0.9,
  },
  {
    id: 'biz-global',
    topicId: 'biz-macro-topic',
    name: 'Kursy walut, handel i Unia Europejska',
    level: 'PR',
    ckeRequirement: 'Kurs walut a handel, protekcjonizm, wspólny rynek UE i strefa euro, internacjonalizacja i globalizacja (ZR II.14, II.18–II.21)',
    prerequisites: ['biz-fiscal-monetary'],
    examValue: 0.75,
  },
];

// ===========================================================================
// Lekcje
// ===========================================================================

export const MACRO_LESSONS: Lesson[] = [
  {
    skillId: 'biz-macro-indicators',
    minutes: 14,
    intro:
      'Żeby ocenić, jak radzi sobie cała gospodarka, ekonomiści patrzą na kilka liczb: ile wytworzono (PKB), jak szybko rosną ceny (inflacja) i ilu ludzi ma pracę. Na egzaminie trzeba je umieć policzyć i zinterpretować.',
    blocks: [
      p('PKB (produkt krajowy brutto) to wartość wszystkich dóbr i usług finalnych wytworzonych na terenie kraju w danym okresie. Do porównań poziomu życia używa się PKB na mieszkańca (per capita), najlepiej według parytetu siły nabywczej.'),
      f('\\text{inflacja} = \\frac{\\text{cena koszyka teraz} - \\text{cena koszyka wcześniej}}{\\text{cena koszyka wcześniej}} \\cdot 100\\%'),
      p('PKB nominalny liczony jest w bieżących cenach, więc rośnie także przez inflację. Realny wzrost otrzymasz wzorem Fishera: $(1 + r) = \\frac{1 + n}{1 + i}$ (n — wzrost nominalny, i — inflacja). W przybliżeniu r ≈ n − i.'),
      p('Wzrost gospodarczy to zmiana ilościowa (więcej produkcji, wyższe PKB). Rozwój gospodarczy obejmuje też zmiany jakościowe: strukturę gospodarki, technologię, poziom życia, edukację. Cykl koniunkturalny ma fazy: ożywienie, rozkwit (szczyt), recesja (spowolnienie) i depresja (dno).'),
      tip('Inflacje z kolejnych lat się mnoży, a nie dodaje: 5% i 10% to $1{,}05 \\cdot 1{,}10 = 1{,}155$, czyli 15,5%.'),
      warn('Spadek inflacji z 10% do 4% nie oznacza spadku cen — ceny nadal rosną, tylko wolniej. Spadek cen to deflacja.'),
    ],
    examples: [
      example(
        'Koszyk dóbr kosztował 2000 zł, a rok później 2090 zł. Ile wyniosła inflacja?',
        [['Wzrost ceny: 90 zł.', 'różnica cen koszyka'], '90 : 2000 = 0,045.'],
        '4,5%',
      ),
      example(
        'PKB nominalny wzrósł o 8%, a inflacja wyniosła 5%. Ile wyniósł realny wzrost PKB?',
        ['$1{,}08 : 1{,}05 = 1{,}0286$.', 'W przybliżeniu 8% − 5% = 3%.'],
        'ok. 2,86%',
      ),
    ],
    pitfalls: ['Dodawanie inflacji z kolejnych lat.', 'Mylenie spadku inflacji ze spadkiem cen.', 'Porównywanie PKB krajów o różnej liczbie ludności bez przeliczenia na mieszkańca.'],
  },
  {
    skillId: 'biz-state-budget',
    minutes: 12,
    intro:
      'Budżet państwa to plan dochodów i wydatków na rok. Państwo zbiera pieniądze głównie z podatków, a wydaje na emerytury, ochronę zdrowia, edukację, obronę czy drogi. Gdy wydaje więcej, niż zbiera, powstaje deficyt — i trzeba go pożyczyć.',
    blocks: [
      p('Największe dochody budżetu państwa w Polsce dają podatki pośrednie — przede wszystkim VAT — oraz akcyza, a z bezpośrednich: CIT i część PIT. Gminy, powiaty i województwa (jednostki samorządu terytorialnego) mają własne dochody (np. podatek od nieruchomości, udziały w PIT i CIT) oraz dotacje i subwencje z budżetu państwa.'),
      f('\\text{deficyt} = \\text{wydatki} - \\text{dochody} \\qquad \\frac{\\text{dług}}{\\text{PKB}} \\cdot 100\\%'),
      p('Dług publiczny to suma zobowiązań sektora finansów publicznych — narasta z kolejnych deficytów. Wysoki dług oznacza wysokie odsetki (koszty obsługi długu), które zabierają pieniądze na inne cele, a pożyczanie przez państwo może podnosić stopy procentowe i ograniczać inwestycje firm (efekt wypychania).'),
      tip('Relację długu lub deficytu do PKB licz zawsze w tych samych jednostkach (np. w mld zł).'),
      warn('Deficyt dotyczy jednego roku, a dług jest „sumą” wielu lat. Spadek deficytu nie oznacza spadku długu — dług wciąż rośnie, tylko wolniej.'),
    ],
    examples: [
      example(
        'Deficyt wyniósł 90 mld zł, a PKB 3600 mld zł. Ile to procent PKB?',
        [['90 : 3600 = 0,025.', 'obie wielkości w mld zł'], 'Razy 100%.'],
        '2,5% PKB',
      ),
      example(
        'Czy zmniejszenie deficytu z 5% do 3% PKB zmniejsza dług publiczny?',
        ['Deficyt 3% nadal trzeba sfinansować pożyczką.', 'Dług rośnie wolniej, ale rośnie.'],
        'nie',
      ),
    ],
    pitfalls: ['Mylenie deficytu z długiem.', 'Uznanie PIT za największe źródło dochodów budżetu państwa.', 'Liczenie procentu PKB w różnych jednostkach.'],
  },
  {
    skillId: 'biz-fiscal-monetary',
    minutes: 16,
    intro:
      'Państwo ma dwa główne zestawy narzędzi wpływu na gospodarkę. Polityka fiskalna (rząd i parlament) to podatki i wydatki publiczne. Polityka monetarna (bank centralny — w Polsce NBP) to przede wszystkim stopy procentowe i ilość pieniądza.',
    blocks: [
      p('Rada Polityki Pieniężnej (RPP) ustala stopy procentowe NBP; główną jest stopa referencyjna. Podwyżka stóp podraża kredyty i zachęca do oszczędzania — konsumpcja i inwestycje hamują, a inflacja spada. Obniżka działa odwrotnie i pobudza gospodarkę. Główny cel NBP to stabilny poziom cen (cel inflacyjny 2,5% ± 1 pkt proc.).'),
      p('Polityka ekspansywna (pobudzająca) — niższe podatki, wyższe wydatki, niższe stopy — stosowana jest w recesji. Restrykcyjna — odwrotnie — przy przegrzaniu i wysokiej inflacji.'),
      f('\\text{podatek (skala)} = 12\\% \\cdot 120\\,000 + 32\\% \\cdot (\\text{dochód} - 120\\,000)', 'Przykład progresji: wyższa stawka tylko od nadwyżki ponad próg.'),
      p('Podatek progresywny: wraz ze wzrostem dochodu rośnie stawka (od nadwyżki). Liniowy: jedna stawka dla każdego dochodu. Podejście etatystyczne zakłada dużą rolę państwa w gospodarce (interwencjonizm, np. dopłaty, regulacja cen), liberalne — małą (państwo pilnuje reguł). Protekcjonizm to ochrona krajowych producentów (cła, kontyngenty).'),
      tip('Przy skali podatkowej wyższą stawkę płaci się tylko od części dochodu powyżej progu — nie od całego dochodu.'),
      warn('Stopy procentowe ustala RPP, a nie rząd ani minister finansów.'),
    ],
    examples: [
      example(
        'Oblicz podatek wg skali 12% do 120 000 zł i 32% od nadwyżki dla dochodu 150 000 zł (bez kwoty wolnej).',
        [['12% · 120 000 = 14 400 zł.', 'pierwszy próg'], '32% · 30 000 = 9600 zł; razem 24 000 zł.'],
        '24 000 zł',
      ),
      example(
        'Inflacja jest wysoka i rośnie. Co zrobi RPP?',
        ['Podwyższy stopy procentowe.', 'Droższy kredyt ogranicza wydatki i hamuje wzrost cen.'],
        'podniesie stopy',
      ),
    ],
    pitfalls: ['Liczenie wyższej stawki od całego dochodu.', 'Przypisanie ustalania stóp rządowi.', 'Pomylenie narzędzi fiskalnych z monetarnymi.'],
  },
  {
    skillId: 'biz-global',
    minutes: 14,
    intro:
      'Polskie firmy kupują i sprzedają za granicą, więc kurs złotego wpływa na ich zyski. Do tego dochodzi wspólny rynek Unii Europejskiej, który pozwala handlować z 26 krajami prawie jak w kraju.',
    blocks: [
      p('Aprecjacja złotego (umocnienie) — za jedno euro płacisz mniej złotych. Import tanieje, a eksporter dostaje mniej złotych za każde euro. Deprecjacja (osłabienie) — odwrotnie: sprzyja eksporterom, szkodzi importerom.'),
      f('\\text{kwota w zł} = \\text{kwota w walucie} \\cdot \\text{kurs}'),
      p('Wspólny rynek UE opiera się na czterech swobodach: przepływu towarów, usług, kapitału i osób. Przedsiębiorca może prowadzić działalność w innym kraju UE na tych samych zasadach co miejscowi. Strefa euro to państwa ze wspólną walutą — rezygnują z własnej polityki monetarnej na rzecz Europejskiego Banku Centralnego.'),
      p('Firmy wchodzą na rynki zagraniczne stopniowo: eksport, licencja, franczyza, spółka joint venture, a na końcu własna filia (inwestycja bezpośrednia) — im dalej, tym większe zaangażowanie kapitału i kontrola. Globalizacja daje dostęp do rynków i tańszych dostaw, ale zaostrza konkurencję.'),
      tip('Pytanie kontrolne przy kursie: czy firma dostaje waluty (eksporter), czy ją wydaje (importer)? Słabszy złoty pomaga temu, kto dostaje walutę.'),
      warn('Polska jest w UE, ale nie w strefie euro — stopy procentowe w Polsce nadal ustala RPP.'),
    ],
    examples: [
      example(
        'Eksporter dostał 10 000 euro. Kurs wzrósł z 4,20 do 4,40 zł. Ile zyskał na zmianie kursu?',
        [['10 000 · 4,40 − 10 000 · 4,20.', 'różnica kursów razy kwota'], '44 000 − 42 000 = 2000 zł.'],
        '2000 zł',
      ),
      example(
        'Co oznacza dla importera aprecjacja złotego?',
        ['Za każde euro płaci mniej złotych.', 'Towary z zagranicy tanieją.'],
        'niższe koszty zakupu',
      ),
    ],
    pitfalls: ['Mylenie aprecjacji z deprecjacją.', 'Uznanie wspólnej waluty za jedną ze swobód rynku UE.', 'Przekonanie, że Polska należy do strefy euro.'],
  },
];

// ===========================================================================
// Zadania
// ===========================================================================

export const MACRO_QUESTIONS: Question[] = [
  // biz-macro-indicators ------------------------------------------------------
  choice({
    id: 'bg-k-1',
    skill: 'biz-macro-indicators',
    kind: 'foundation',
    difficulty: 1,
    prompt: 'Czym jest produkt krajowy brutto (PKB)?',
    choices: [
      'wartością dóbr i usług finalnych wytworzonych na terenie kraju w danym okresie',
      'sumą wszystkich podatków zebranych przez państwo w ciągu roku',
      'wartością towarów wyeksportowanych za granicę',
      'majątkiem wszystkich obywateli kraju',
    ],
    answer: 'A',
    hints: ['Czy PKB mierzy produkcję, dochody państwa czy majątek?', 'Produkcję w danym okresie.', 'Co znaczy „krajowy”?', 'Liczy się miejsce wytworzenia — teren kraju.'],
    steps: ['PKB to wartość produkcji dóbr i usług finalnych.', 'Liczy się to, co wytworzono na terenie kraju w danym okresie.'],
    errors: [
      ['B', 'Podatki to dochody budżetu.', 'PKB mierzy produkcję.'],
      ['C', 'Eksport to tylko część produkcji.', 'PKB obejmuje całą produkcję finalną.'],
      ['D', 'Majątek to zasób, PKB to przepływ w okresie.', 'PKB mierzy produkcję w danym czasie.'],
    ],
  }),
  numeric({
    id: 'bg-k-2',
    skill: 'biz-macro-indicators',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Koszyk dóbr konsumpcyjnych kosztował 2000 zł, a rok później 2090 zł. Oblicz stopę inflacji w tym roku (w %).',
    answer: '4,5',
    variants: ['4.5'],
    tolerance: 0.001,
    verify: () => ((2090 - 2000) / 2000) * 100,
    hints: ['O ile złotych podrożał koszyk?', 'O różnicę cen z obu lat.', 'Do jakiej wartości porównujesz ten wzrost?', 'Do ceny z początku okresu — potem pomnóż przez 100%.'],
    steps: ['Wzrost: 2090 − 2000 = 90 zł.', '90 : 2000 · 100% = 4,5%.'],
    errors: [['90', 'Podany wzrost w złotych zamiast w procentach.', 'Inflacja to wzrost względny.']],
  }),
  choice({
    id: 'bg-k-3',
    skill: 'biz-macro-indicators',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Czym rozwój gospodarczy różni się od wzrostu gospodarczego?',
    choices: ['obejmuje też zmiany jakościowe, np. technologii i poziomu życia', 'oznacza wyłącznie wzrost PKB w danym roku', 'dotyczy krajów biednych, a wzrost — krajów bogatych', 'nie różni się — to dwa określenia tego samego zjawiska'],
    answer: 'A',
    hints: ['Czy wzrost PKB zawsze oznacza lepsze życie ludzi?', 'Nie zawsze — PKB mierzy tylko ilość produkcji.', 'Co jeszcze może się zmieniać w gospodarce?', 'Technologia, struktura, edukacja, jakość życia.'],
    steps: ['Wzrost to zmiana ilościowa (np. PKB).', 'Rozwój obejmuje też zmiany jakościowe.'],
    errors: [
      ['B', 'To opis wzrostu, nie rozwoju.', 'Rozwój to także zmiany jakościowe.'],
      ['C', 'Oba pojęcia dotyczą każdej gospodarki.', 'Rozwój jest szerszy niż wzrost.'],
      ['D', 'Pojęcia się różnią.', 'Rozwój jest szerszy niż wzrost.'],
    ],
  }),
  choice({
    id: 'bg-k-4',
    skill: 'biz-macro-indicators',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Spadek produkcji, rosnące bezrobocie i ograniczanie inwestycji przez firmy to typowe cechy fazy cyklu koniunkturalnego nazywanej:',
    choices: ['recesją', 'ożywieniem', 'rozkwitem', 'boomem inwestycyjnym'],
    answer: 'A',
    hints: ['Czy opisane zjawiska świadczą o poprawie, czy o pogorszeniu koniunktury?', 'O pogorszeniu.', 'Która faza następuje po szczycie?', 'Gospodarka zwalnia i się kurczy.'],
    steps: ['Spadek produkcji i inwestycji oraz rosnące bezrobocie oznaczają pogorszenie koniunktury.', 'To recesja.'],
    errors: [
      ['B', 'W ożywieniu produkcja rośnie.', 'Opis dotyczy recesji.'],
      ['C', 'Rozkwit to szczyt koniunktury.', 'Opis dotyczy recesji.'],
      ['D', 'Boom to wzrost inwestycji.', 'Opis dotyczy recesji.'],
    ],
  }),
  numeric({
    id: 'bg-k-5',
    skill: 'biz-macro-indicators',
    kind: 'typical',
    difficulty: 3,
    prompt: 'PKB kraju wyniósł 3600 mld zł, a liczba ludności 37,5 mln. Oblicz PKB na mieszkańca (w zł).',
    answer: 96000,
    verify: () => (3600 * 1e9) / (37.5 * 1e6),
    hints: ['Jak podzielić PKB między mieszkańców?', 'Podziel PKB przez liczbę ludności.', 'Uważaj na jednostki: mld to $10^9$, mln to $10^6$.', 'Najprościej: 3600 mld : 37,5 mln = 3600 000 : 37,5 zł.'],
    steps: ['3600 mld zł = 3 600 000 mln zł.', '3 600 000 : 37,5 = 96 000 zł na mieszkańca.'],
    errors: [['96', 'Pomylone jednostki (mld i mln).', 'mld = 1000 mln.']],
  }),
  numeric({
    id: 'bg-k-6',
    skill: 'biz-macro-indicators',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'PKB nominalny wzrósł w ciągu roku o 8%, a inflacja wyniosła 5%. Oblicz realny wzrost PKB dokładnie (wzorem Fishera), w %, z dokładnością do dwóch miejsc po przecinku.',
    answer: '2,86',
    variants: ['2.86'],
    tolerance: 0.005,
    verify: () => Math.round((1.08 / 1.05 - 1) * 10000) / 100,
    hints: ['Dlaczego nie wystarczy odjąć 5 od 8?', 'To tylko przybliżenie — dokładny wzór dzieli wskaźniki.', '$1 + r = \\frac{1{,}08}{1{,}05}$.', 'Odejmij 1 i zamień na procenty.'],
    steps: ['$1{,}08 : 1{,}05 \\approx 1{,}0286$.', 'Realny wzrost ≈ 2,86%.'],
    errors: [['3', 'Użyte przybliżenie n − i.', 'Dokładny wzór: (1 + n) / (1 + i) − 1.']],
  }),
  numeric({
    id: 'bg-k-7',
    skill: 'biz-macro-indicators',
    kind: 'transfer',
    difficulty: 5,
    prompt: 'W pierwszym roku inflacja wyniosła 5%, a w drugim 10%. O ile procent łącznie wzrosły ceny w ciągu dwóch lat?',
    answer: '15,5',
    variants: ['15.5'],
    tolerance: 0.001,
    verify: () => Math.round((1.05 * 1.1 - 1) * 1000) / 10,
    hints: ['Czy inflacje z kolejnych lat można po prostu dodać?', 'Nie — drugi wzrost liczy się od wyższej już ceny.', 'Pomnóż wskaźniki: $1{,}05 \\cdot 1{,}10$.', 'Odejmij 1 i zamień na procenty.'],
    steps: ['$1{,}05 \\cdot 1{,}10 = 1{,}155$.', 'Ceny wzrosły łącznie o 15,5%.'],
    errors: [['15', 'Inflacje dodane zamiast pomnożone.', 'Drugi wzrost liczy się od podwyższonej ceny.']],
  }),

  // biz-state-budget ----------------------------------------------------------
  choice({
    id: 'bg-b-1',
    skill: 'biz-state-budget',
    kind: 'foundation',
    difficulty: 1,
    prompt: 'Kiedy w budżecie państwa powstaje deficyt?',
    choices: ['gdy wydatki w danym roku są większe od dochodów', 'gdy dochody w danym roku są większe od wydatków', 'gdy państwo spłaca raty zaciągniętego wcześniej długu', 'gdy inflacja przekracza cel ustalony przez NBP'],
    answer: 'A',
    hints: ['Co znaczy słowo „deficyt”?', 'Niedobór, brak.', 'Czego brakuje w budżecie?', 'Dochodów na pokrycie wydatków.'],
    steps: ['Deficyt = wydatki − dochody > 0.', 'Brakującą kwotę państwo pożycza.'],
    errors: [
      ['B', 'To nadwyżka budżetowa.', 'Deficyt: wydatki większe od dochodów.'],
      ['C', 'Spłata długu to osobna operacja.', 'Deficyt dotyczy relacji wydatków do dochodów.'],
      ['D', 'Inflacja to zjawisko cenowe.', 'Deficyt dotyczy budżetu.'],
    ],
  }),
  choice({
    id: 'bg-b-2',
    skill: 'biz-state-budget',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Który podatek przynosi budżetowi państwa w Polsce najwięcej dochodów?',
    choices: ['podatek od towarów i usług (VAT)', 'podatek od nieruchomości', 'podatek od spadków i darowizn', 'opłata targowa'],
    answer: 'A',
    hints: ['Który podatek płacisz przy niemal każdym zakupie?', 'Jest doliczany do ceny towarów i usług.', 'Podatek od nieruchomości trafia do gminy, a nie do budżetu państwa.', 'Największe dochody dają podatki pośrednie.'],
    steps: ['VAT to podatek pośredni płacony przy zakupach — daje budżetowi państwa najwięcej.', 'Podatek od nieruchomości i opłata targowa to dochody gmin.'],
    errors: [
      ['B', 'To dochód gminy.', 'Największy dochód budżetu państwa to VAT.'],
      ['C', 'To podatek o niewielkich wpływach.', 'Największy dochód budżetu państwa to VAT.'],
      ['D', 'To dochód gminy.', 'Największy dochód budżetu państwa to VAT.'],
    ],
  }),
  text({
    id: 'bg-b-3',
    skill: 'biz-state-budget',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Uzupełnij jednym słowem: suma zobowiązań sektora finansów publicznych, narastająca z kolejnych deficytów, to dług ________.',
    answer: 'publiczny',
    hints: ['Czyj to dług — firmy, obywatela czy państwa?', 'Sektora finansów publicznych.', 'Jak nazywa się dług instytucji publicznych?', 'Przymiotnik od słowa „publiczne”.'],
    steps: ['Zobowiązania sektora finansów publicznych to dług publiczny.', 'Narasta, gdy kolejne budżety mają deficyt.'],
    errors: [['budżetowy', 'Pomylono dług z deficytem budżetowym.', 'Suma zobowiązań to dług publiczny.']],
  }),
  numeric({
    id: 'bg-b-4',
    skill: 'biz-state-budget',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Dochody budżetu państwa wyniosły 600 mld zł, wydatki 690 mld zł, a PKB 3600 mld zł. Oblicz deficyt budżetowy jako procent PKB.',
    answer: '2,5',
    variants: ['2.5'],
    tolerance: 0.001,
    verify: () => ((690 - 600) / 3600) * 100,
    hints: ['Ile wyniósł deficyt w mld zł?', 'Wydatki minus dochody.', 'Do czego porównujesz deficyt?', 'Do PKB — podziel i pomnóż przez 100%.'],
    steps: ['Deficyt: 690 − 600 = 90 mld zł.', '90 : 3600 · 100% = 2,5% PKB.'],
    errors: [['15', 'Deficyt odniesiony do dochodów budżetu.', 'Relację liczy się do PKB.']],
  }),
  choice({
    id: 'bg-b-5',
    skill: 'biz-state-budget',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Który z wymienionych dochodów jest dochodem własnym gminy?',
    choices: ['podatek od nieruchomości', 'podatek akcyzowy od paliw', 'cło od towarów spoza UE', 'podatek VAT od usług'],
    answer: 'A',
    hints: ['Kto płaci podatek od domu albo mieszkania i komu?', 'Właściciel — gminie, w której leży nieruchomość.', 'Akcyza, cło i VAT to podatki centralne.', 'Które z nich trafiają do budżetu państwa?'],
    steps: ['Podatek od nieruchomości to dochód własny gminy.', 'VAT i akcyza trafiają do budżetu państwa, cła — do budżetu UE.'],
    errors: [
      ['B', 'Akcyza to dochód budżetu państwa.', 'Dochód gminy to podatek od nieruchomości.'],
      ['C', 'Cła trafiają do budżetu UE.', 'Dochód gminy to podatek od nieruchomości.'],
      ['D', 'VAT to dochód budżetu państwa.', 'Dochód gminy to podatek od nieruchomości.'],
    ],
  }),
  choice({
    id: 'bg-b-6',
    skill: 'biz-state-budget',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Dlaczego wysoki dług publiczny może ograniczać rozwój gospodarki?',
    choices: ['odsetki zabierają środki na inne wydatki i mogą podrażać kredyt dla firm', 'cały dług trzeba spłacić w ciągu jednego roku budżetowego', 'państwo z wysokim długiem nie może już emitować obligacji', 'każdy obywatel musi osobiście spłacić swoją część długu'],
    answer: 'A',
    hints: ['Czy od długu płaci się odsetki?', 'Tak — to koszty obsługi długu.', 'Skąd państwo bierze pieniądze na odsetki?', 'Z budżetu, kosztem innych wydatków; do tego konkuruje z firmami o oszczędności.'],
    steps: ['Odsetki od długu obciążają budżet i ograniczają inne wydatki.', 'Duże potrzeby pożyczkowe państwa mogą podnosić stopy i wypychać inwestycje prywatne.'],
    errors: [
      ['B', 'Dług spłaca się latami, często nowymi obligacjami.', 'Problemem są koszty obsługi długu.'],
      ['C', 'Państwo zwykle nadal pożycza, tylko drożej.', 'Problemem są koszty obsługi długu.'],
      ['D', 'Dług spłaca państwo z budżetu, nie obywatele osobiście.', 'Chodzi o koszty obsługi długu i koszt kredytu.'],
    ],
  }),
  numeric({
    id: 'bg-b-7',
    skill: 'biz-state-budget',
    kind: 'transfer',
    difficulty: 5,
    prompt: 'Na początku roku dług publiczny wynosił 1800 mld zł. W ciągu roku deficyt wyniósł 150 mld zł (w całości sfinansowany nowym długiem), a PKB wyniósł 3960 mld zł. Oblicz relację długu do PKB na koniec roku (w %, z dokładnością do jednego miejsca po przecinku).',
    answer: '49,2',
    variants: ['49.2'],
    tolerance: 0.05,
    verify: () => Math.round(((1800 + 150) / 3960) * 1000) / 10,
    hints: ['Ile wynosi dług na koniec roku?', 'Dług początkowy plus deficyt sfinansowany pożyczką.', 'Podziel dług przez PKB.', 'Wynik pomnóż przez 100% i zaokrąglij.'],
    steps: ['Dług na koniec roku: 1800 + 150 = 1950 mld zł.', '1950 : 3960 · 100% ≈ 49,2% PKB.'],
    errors: [['45,5', 'Pominięty tegoroczny deficyt.', 'Deficyt powiększa dług.']],
  }),

  // biz-fiscal-monetary -------------------------------------------------------
  choice({
    id: 'bg-f-1',
    skill: 'biz-fiscal-monetary',
    kind: 'foundation',
    difficulty: 1,
    prompt: 'Który organ ustala w Polsce stopy procentowe Narodowego Banku Polskiego?',
    choices: ['Rada Polityki Pieniężnej', 'Minister Finansów', 'Sejm w ustawie budżetowej', 'Komisja Nadzoru Finansowego'],
    answer: 'A',
    hints: ['Czy stopy procentowe to narzędzie polityki fiskalnej, czy monetarnej?', 'Monetarnej — prowadzonej przez bank centralny.', 'Który organ NBP decyduje o polityce pieniężnej?', 'Jego nazwa zawiera słowa „polityka pieniężna”.'],
    steps: ['Stopy procentowe NBP ustala Rada Polityki Pieniężnej.', 'Rząd i Sejm prowadzą politykę fiskalną, KNF nadzoruje rynek finansowy.'],
    errors: [
      ['B', 'Minister Finansów odpowiada za politykę fiskalną.', 'Stopy ustala RPP.'],
      ['C', 'Sejm uchwala budżet.', 'Stopy ustala RPP.'],
      ['D', 'KNF nadzoruje banki i rynek kapitałowy.', 'Stopy ustala RPP.'],
    ],
  }),
  choice({
    id: 'bg-f-2',
    skill: 'biz-fiscal-monetary',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Które działanie jest narzędziem polityki fiskalnej?',
    choices: ['obniżenie stawki podatku dochodowego', 'podwyższenie stopy referencyjnej NBP', 'zmiana stopy rezerwy obowiązkowej', 'sprzedaż bonów pieniężnych przez NBP'],
    answer: 'A',
    hints: ['Czym zajmuje się polityka fiskalna?', 'Podatkami i wydatkami publicznymi.', 'Kto prowadzi pozostałe działania?', 'Bank centralny — to polityka monetarna.'],
    steps: ['Stawki podatków to narzędzie fiskalne.', 'Stopy procentowe, rezerwa obowiązkowa i operacje otwartego rynku to narzędzia monetarne.'],
    errors: [
      ['B', 'To narzędzie polityki monetarnej.', 'Fiskalne to podatki i wydatki.'],
      ['C', 'To narzędzie polityki monetarnej.', 'Fiskalne to podatki i wydatki.'],
      ['D', 'To operacja otwartego rynku — polityka monetarna.', 'Fiskalne to podatki i wydatki.'],
    ],
  }),
  choice({
    id: 'bg-f-3',
    skill: 'biz-fiscal-monetary',
    kind: 'typical',
    difficulty: 3,
    prompt: 'RPP podwyższyła stopy procentowe. Jaki jest najbardziej prawdopodobny skutek?',
    choices: ['kredyty podrożeją, a presja inflacyjna osłabnie', 'kredyty stanieją, a konsumpcja wyraźnie wzrośnie', 'inflacja natychmiast zamieni się w deflację', 'złoty osłabnie, a inflacja przyspieszy'],
    answer: 'A',
    hints: ['Co stopa procentowa oznacza dla kredytobiorcy?', 'Wyższy koszt pożyczonych pieniędzy.', 'A dla oszczędzającego?', 'Wyższe oprocentowanie lokat — ludzie mniej wydają, ceny rosną wolniej.'],
    steps: ['Wyższe stopy podrażają kredyt i zachęcają do oszczędzania.', 'Spada popyt, a z nim presja na wzrost cen.'],
    errors: [
      ['B', 'Odwrócony kierunek zmian.', 'Podwyżka stóp podraża kredyty.'],
      ['C', 'Efekt jest stopniowy — inflacja zwalnia.', 'Nie oznacza od razu spadku cen.'],
      ['D', 'Wyższe stopy zwykle umacniają walutę i hamują inflację.', 'Podwyżka stóp studzi popyt.'],
    ],
  }),
  numeric({
    id: 'bg-f-4',
    skill: 'biz-fiscal-monetary',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Przyjmij uproszczoną skalę podatkową: 12% od dochodu do 120 000 zł i 32% od nadwyżki ponad 120 000 zł (pomiń kwotę wolną). Oblicz podatek od rocznego dochodu 150 000 zł (w zł).',
    answer: 24000,
    verify: () => 0.12 * 120000 + 0.32 * (150000 - 120000),
    hints: ['Od jakiej części dochodu płaci się 32%?', 'Tylko od nadwyżki ponad 120 000 zł.', 'Policz osobno podatek od pierwszych 120 000 zł i od nadwyżki.', 'Zsumuj obie części.'],
    steps: ['12% · 120 000 = 14 400 zł; 32% · 30 000 = 9600 zł.', 'Podatek: 14 400 + 9600 = 24 000 zł.'],
    errors: [['48000', '32% policzone od całego dochodu.', 'Wyższa stawka dotyczy tylko nadwyżki.']],
  }),
  numeric({
    id: 'bg-f-5',
    skill: 'biz-fiscal-monetary',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Przedsiębiorca osiąga dochód 150 000 zł rocznie. Porównaj podatek liniowy 19% z uproszczoną skalą (12% do 120 000 zł, 32% od nadwyżki; bez kwoty wolnej). O ile złotych więcej zapłaci przy podatku liniowym?',
    answer: 4500,
    verify: () => 0.19 * 150000 - (0.12 * 120000 + 0.32 * 30000),
    hints: ['Ile wynosi podatek liniowy?', '19% od całego dochodu.', 'Ile wynosi podatek wg skali?', 'Od pierwszych 120 000 zł stawka 12%, od nadwyżki 32% — potem odejmij.'],
    steps: ['Liniowy: 19% · 150 000 = 28 500 zł; skala: 14 400 + 9600 = 24 000 zł.', 'Różnica: 28 500 − 24 000 = 4500 zł.'],
    errors: [['-4500', 'Odjęte w złej kolejności.', 'Pytanie: o ile więcej przy liniowym.']],
  }),
  choice({
    id: 'bg-f-6',
    skill: 'biz-fiscal-monetary',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Które działanie jest przykładem interwencjonizmu państwowego?',
    choices: ['ustalenie przez państwo maksymalnej ceny energii', 'obniżka cen przez sieć sklepów w ramach promocji', 'wzrost cen energii na giełdzie po awarii elektrowni', 'umowa dwóch firm o wspólnej dostawie energii'],
    answer: 'A',
    hints: ['Co oznacza interwencja państwa w gospodarkę?', 'Państwo zmienia wynik działania rynku.', 'Które działanie wykonuje państwo, a które uczestnicy rynku?', 'Regulacja cen to typowa interwencja.'],
    steps: ['Interwencjonizm to ingerencja państwa w mechanizm rynkowy.', 'Ustalanie cen maksymalnych jest taką ingerencją.'],
    errors: [
      ['B', 'To decyzja prywatnej firmy.', 'Interwencjonizm to działanie państwa.'],
      ['C', 'To zjawisko rynkowe.', 'Interwencjonizm to działanie państwa.'],
      ['D', 'To swoboda umów.', 'Interwencjonizm to działanie państwa.'],
    ],
  }),
  choice({
    id: 'bg-f-7',
    skill: 'biz-fiscal-monetary',
    kind: 'transfer',
    difficulty: 5,
    prompt: 'Gospodarka jest w recesji, a inflacja niska. Które połączenie działań najsilniej ją pobudzi?',
    choices: ['obniżenie stóp procentowych i zwiększenie wydatków publicznych', 'podwyższenie stóp procentowych i podwyżka podatków', 'podwyższenie stóp procentowych i obniżka podatków', 'obniżenie wydatków publicznych i podwyżka podatków'],
    answer: 'A',
    hints: ['Jak nazywa się polityka, która pobudza gospodarkę?', 'Ekspansywna.', 'Co robi ekspansywna polityka monetarna?', 'Obniża stopy; ekspansywna fiskalna zwiększa wydatki lub obniża podatki.'],
    steps: ['W recesji stosuje się politykę ekspansywną.', 'Niższe stopy i wyższe wydatki publiczne zwiększają popyt.'],
    errors: [
      ['B', 'To polityka restrykcyjna.', 'W recesji potrzebna jest ekspansywna.'],
      ['C', 'Wyższe stopy hamują gospodarkę.', 'Oba działania powinny pobudzać.'],
      ['D', 'To polityka restrykcyjna.', 'W recesji potrzebna jest ekspansywna.'],
    ],
  }),
  text({
    id: 'bg-f-8',
    skill: 'biz-fiscal-monetary',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Uzupełnij jednym słowem: główną stopą procentową NBP, wyznaczającą rentowność bonów pieniężnych i wpływającą na oprocentowanie kredytów, jest stopa ________.',
    answer: 'referencyjna',
    hints: ['Jaką stopę media podają jako „stopę NBP”?', 'Tę, do której odnoszą się inne stopy.', 'Od czego pochodzi słowo „odniesienia” po łacinie?', 'Od „referencja”.'],
    steps: ['Główną stopą NBP jest stopa referencyjna.', 'Pozostałe (lombardowa, depozytowa) wyznaczają korytarz wokół niej.'],
    errors: [['lombardowa', 'Stopa lombardowa to górna granica korytarza.', 'Główna stopa to referencyjna.']],
  }),

  // biz-global ----------------------------------------------------------------
  choice({
    id: 'bg-g-1',
    skill: 'biz-global',
    kind: 'foundation',
    difficulty: 1,
    prompt: 'Złoty umocnił się wobec euro (aprecjacja). Co to oznacza dla polskiego importera kupującego towary w strefie euro?',
    choices: ['zapłaci mniej złotych za te same towary', 'zapłaci więcej złotych za te same towary', 'nie będzie mógł kupować w euro', 'nic się dla niego nie zmieni'],
    answer: 'A',
    hints: ['Co oznacza umocnienie złotego?', 'Za jedno euro płaci się mniej złotych.', 'Importer płaci w euro, ale ma złote.', 'Ile złotych wyda na tę samą kwotę w euro?'],
    steps: ['Aprecjacja: euro kosztuje mniej złotych.', 'Importer wydaje mniej złotych na ten sam towar.'],
    errors: [
      ['B', 'To skutek deprecjacji złotego.', 'Silniejszy złoty obniża koszt importu.'],
      ['C', 'Kurs nie zakazuje handlu.', 'Zmienia koszt w złotych.'],
      ['D', 'Kurs wpływa na koszt importu w złotych.', 'Import tanieje.'],
    ],
  }),
  numeric({
    id: 'bg-g-2',
    skill: 'biz-global',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Firma kupiła maszynę za 2000 euro. Kurs wynosi 4,30 zł za 1 euro. Ile złotych zapłaciła?',
    answer: 8600,
    verify: () => 2000 * 4.3,
    hints: ['Co oznacza kurs 4,30?', 'Jedno euro kosztuje 4,30 zł.', 'Ile takich euro trzeba kupić?', 'Pomnóż kwotę w euro przez kurs.'],
    steps: ['2000 euro · 4,30 zł/euro.', '= 8600 zł.'],
    errors: [['465', 'Kwota w euro podzielona przez kurs.', 'Przeliczenie na złote to mnożenie przez kurs.']],
  }),
  choice({
    id: 'bg-g-3',
    skill: 'biz-global',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Złoty osłabił się wobec dolara (deprecjacja). Kto na tym najczęściej korzysta?',
    choices: ['polski eksporter sprzedający towary za dolary', 'polski importer kupujący towary za dolary', 'polski turysta wyjeżdżający do USA', 'firma spłacająca kredyt w dolarach'],
    answer: 'A',
    hints: ['Kto dostaje dolary, a kto je wydaje?', 'Eksporter dostaje dolary za towar.', 'Ile złotych dostanie za każdego dolara po osłabieniu złotego?', 'Więcej — jego przychód w złotych rośnie.'],
    steps: ['Po deprecjacji dolar kosztuje więcej złotych.', 'Eksporter wymienia dolary na więcej złotych; importer, turysta i dłużnik w dolarach tracą.'],
    errors: [
      ['B', 'Importer płaci więcej złotych.', 'Zyskuje ten, kto dostaje walutę.'],
      ['C', 'Turysta płaci więcej złotych za dolary.', 'Zyskuje eksporter.'],
      ['D', 'Rata w dolarach kosztuje więcej złotych.', 'Zyskuje eksporter.'],
    ],
  }),
  numeric({
    id: 'bg-g-4',
    skill: 'biz-global',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Eksporter ma otrzymać zapłatę 10 000 euro. W dniu zawarcia umowy kurs wynosił 4,20 zł, a w dniu zapłaty 4,40 zł za 1 euro. O ile złotych więcej otrzymał dzięki zmianie kursu?',
    answer: 2000,
    verify: () => 10000 * (4.4 - 4.2),
    hints: ['Ile złotych byłoby przy pierwszym kursie?', '10 000 · 4,20.', 'A ile przy drugim?', 'Odejmij wyniki albo pomnóż 10 000 przez różnicę kursów.'],
    steps: ['10 000 · 4,40 = 44 000 zł; 10 000 · 4,20 = 42 000 zł.', 'Różnica: 2000 zł.'],
    errors: [['0,2', 'Podana różnica kursów.', 'Trzeba pomnożyć ją przez kwotę w euro.']],
  }),
  choice({
    id: 'bg-g-5',
    skill: 'biz-global',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Państwo wprowadziło wysokie cło na importowane rowery. Który skutek jest najbardziej prawdopodobny?',
    choices: ['krajowi producenci zyskają, a konsumenci zapłacą więcej', 'rowery stanieją, bo krajowi producenci zwiększą produkcję', 'import rowerów wzrośnie, bo państwo zarobi na cle', 'cło zapłaci zagraniczny producent, więc ceny się nie zmienią'],
    answer: 'A',
    hints: ['Jak cło zmienia cenę rowerów z importu?', 'Podnosi ją.', 'Kto zyskuje, gdy import drożeje?', 'Krajowi producenci — mają słabszą konkurencję.'],
    steps: ['Cło podraża import i chroni krajowych producentów (protekcjonizm).', 'Konsumenci płacą więcej i mają mniejszy wybór.'],
    errors: [
      ['B', 'Cło podnosi ceny importu, a krajowi producenci mogą podnieść swoje.', 'Rowery podrożeją.'],
      ['C', 'Droższy import zmniejsza jego wielkość.', 'Import spadnie.'],
      ['D', 'Cło zwykle przerzucane jest na nabywców.', 'Ceny wzrosną.'],
    ],
  }),
  choice({
    id: 'bg-g-6',
    skill: 'biz-global',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Które z wymienionych NIE jest jedną z czterech swobód wspólnego rynku Unii Europejskiej?',
    choices: ['obowiązek używania euro w każdym państwie UE', 'swobodny przepływ towarów między państwami UE', 'swobodny przepływ kapitału i płatności', 'swobodny przepływ osób, w tym pracowników'],
    answer: 'A',
    hints: ['Jakie cztery rzeczy mogą swobodnie przepływać w UE?', 'Towary, usługi, kapitał i osoby.', 'Czy wszystkie kraje UE mają euro?', 'Nie — Polska ma złotego.'],
    steps: ['Cztery swobody: towary, usługi, kapitał, osoby.', 'Wspólna waluta obowiązuje tylko w strefie euro i nie jest swobodą rynku.'],
    errors: [
      ['B', 'To jedna z czterech swobód.', 'Szukamy tego, co swobodą nie jest.'],
      ['C', 'To jedna z czterech swobód.', 'Szukamy tego, co swobodą nie jest.'],
      ['D', 'To jedna z czterech swobód.', 'Szukamy tego, co swobodą nie jest.'],
    ],
  }),
  choice({
    id: 'bg-g-7',
    skill: 'biz-global',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Jaki jest najważniejszy gospodarczy koszt wejścia kraju do strefy euro?',
    choices: ['utrata własnej polityki pieniężnej — stopy ustala EBC', 'wyższe cła w handlu z pozostałymi krajami UE', 'utrata kontroli nad wysokością krajowych podatków', 'koszt wymiany waluty w handlu ze strefą euro'],
    answer: 'A',
    hints: ['Kto ustala stopy procentowe dla euro?', 'Europejski Bank Centralny, wspólnie dla całej strefy.', 'Czy kraj może wtedy sam reagować stopami na swoją sytuację?', 'Nie — to cena wspólnej waluty.'],
    steps: ['W strefie euro politykę monetarną prowadzi EBC.', 'Kraj traci możliwość samodzielnego ustalania stóp i kursu.'],
    errors: [
      ['B', 'W UE nie ma ceł wewnętrznych — niezależnie od waluty.', 'Kosztem jest utrata polityki monetarnej.'],
      ['C', 'Podatki pozostają w gestii państw.', 'Kosztem jest utrata polityki monetarnej.'],
      ['D', 'Wejście do strefy euro likwiduje ten koszt — to korzyść, nie koszt.', 'Kosztem jest utrata polityki monetarnej.'],
    ],
  }),
  choice({
    id: 'bg-g-8',
    skill: 'biz-global',
    kind: 'transfer',
    difficulty: 5,
    prompt: 'Polska firma chce wejść na rynek niemiecki. Która forma internacjonalizacji wymaga NAJWIĘKSZEGO zaangażowania kapitału, ale daje największą kontrolę nad działalnością?',
    choices: ['zbudowanie własnego zakładu produkcyjnego w Niemczech', 'eksport towarów przez niemieckiego pośrednika', 'udzielenie licencji niemieckiej firmie', 'sprzedaż franczyzy niemieckiemu przedsiębiorcy'],
    answer: 'A',
    hints: ['Która forma wymaga inwestycji we własny majątek za granicą?', 'Budowa własnego zakładu.', 'Przy której formie firma najmniej polega na partnerach?', 'Im większa inwestycja, tym większa kontrola i ryzyko.'],
    steps: ['Własny zakład za granicą to bezpośrednia inwestycja zagraniczna.', 'Wymaga najwięcej kapitału, ale daje pełną kontrolę; eksport, licencja i franczyza — mniej.'],
    errors: [
      ['B', 'Eksport przez pośrednika wymaga najmniej zaangażowania.', 'Największe zaangażowanie to własna filia.'],
      ['C', 'Licencja przekazuje działalność partnerowi.', 'Największe zaangażowanie to własna filia.'],
      ['D', 'Franczyza opiera się na kapitale franczyzobiorcy.', 'Największe zaangażowanie to własna filia.'],
    ],
  }),
];

// ===========================================================================
// Fiszki
// ===========================================================================

export const MACRO_CARDS: Flashcard[] = [
  card('c-bg-k-1', 'biz-macro-indicators', 'wzor', 'Realny wzrost dokładnie?', '$1 + r = \\frac{1 + n}{1 + i}$ (w przybliżeniu r ≈ n − i).'),
  card('c-bg-k-2', 'biz-macro-indicators', 'definicja', 'Cztery fazy cyklu koniunkturalnego?', 'Ożywienie, rozkwit (szczyt), recesja, depresja (dno).'),

  card('c-bg-b-1', 'biz-state-budget', 'definicja', 'Deficyt a dług publiczny?', 'Deficyt: wydatki − dochody w roku. Dług: suma zobowiązań narosłych z deficytów.'),
  card('c-bg-b-2', 'biz-state-budget', 'definicja', 'Największy dochód budżetu państwa?', 'VAT (podatki pośrednie).'),

  card('c-bg-f-1', 'biz-fiscal-monetary', 'definicja', 'Kto ustala stopy procentowe NBP?', 'Rada Polityki Pieniężnej (RPP); główna stopa: referencyjna.'),
  card('c-bg-f-2', 'biz-fiscal-monetary', 'pulapka', 'Skala podatkowa — od czego wyższa stawka?', 'Tylko od nadwyżki ponad próg, nie od całego dochodu.'),

  card('c-bg-g-1', 'biz-global', 'metoda', 'Kto zyskuje na słabym złotym?', 'Eksporter (dostaje walutę). Traci importer i dłużnik w walucie.'),
  card('c-bg-g-2', 'biz-global', 'definicja', 'Cztery swobody rynku UE?', 'Przepływ towarów, usług, kapitału i osób.'),
];
